// src/encounter/lib/cps-rule.ts
// PROD-1384 — the cps-rule evaluator for conditional (Type-A) encounter-template selection.
//
// Type-A `ConditionalEncounter` templates carry a `cps-rule` (root extension valueString) that is
// meant to swap the template in when the rule evaluates true against the encounter's facts. No
// evaluator existed anywhere (QB only STORES the rule; the BFF selects by Default+age+gender+org and
// never evaluates it — see PROD-1383). This is that evaluator. It lives in the PA lib (pure +
// re-pointable): if a server-side evaluator is built later (BFF/QB), the selection can move there
// and this becomes the client fallback.
//
// Grammar (json-rules-engine-shaped, grounded in the live PROD-1365 smoke):
//   • always           — {"op":"always"}                         (the unconditional default)
//   • condition (leaf) — {"fact":"bmi.value","operator":"greaterThanInclusive","value":30}
//   • group            — {"all":[...]} | {"any":[...]}           (nestable)
// Facts are a context object; a leaf's `fact` is a dotted path resolved against it
// (`bmi.value` → facts.bmi.value). Unknown operator / missing fact / malformed rule → FALSE
// (fail-closed: an un-evaluatable conditional never wins, so the always-default stays the fallback).

/** A leaf condition: a fact path, an operator, and a comparison value. */
export interface CpsCondition {
  fact: string;
  operator: string;
  value: unknown;
}
/** A boolean group — `all` (AND) or `any` (OR) of sub-rules. */
export interface CpsGroup {
  all?: CpsRule[];
  any?: CpsRule[];
}
/** The unconditional default — applies to everyone. */
export interface CpsAlways {
  op: 'always';
}
export type CpsRule = CpsAlways | CpsGroup | CpsCondition;

/** The fact context the rule is evaluated against (e.g. `{ bmi: { value: 31 }, age: 40 }`). */
export type CpsFacts = Record<string, unknown>;

/** Parse a cps-rule `valueString` to an object; null when absent/empty/malformed (caller fails closed). */
export function parseCpsRule(valueString: string | null | undefined): CpsRule | null {
  if (!valueString) return null;
  try {
    const parsed = JSON.parse(valueString);
    return parsed && typeof parsed === 'object' ? (parsed as CpsRule) : null;
  } catch {
    return null;
  }
}

/** Is this the unconditional default rule (`{"op":"always"}`)? */
export function isAlwaysRule(rule: CpsRule | null): boolean {
  return !!rule && (rule as CpsAlways).op === 'always';
}

/** Resolve a dotted fact path (`bmi.value`) against the context; undefined when any segment is missing. */
function resolveFact(facts: CpsFacts, path: string): unknown {
  let cur: unknown = facts;
  for (const seg of path.split('.')) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[seg];
  }
  return cur;
}

/** Numeric comparison helper — both operands must be finite numbers, else the comparison is false. */
function numericCompare(a: unknown, b: unknown, cmp: (x: number, y: number) => boolean): boolean {
  const x = typeof a === 'number' ? a : Number(a);
  const y = typeof b === 'number' ? b : Number(b);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return false;
  return cmp(x, y);
}

/** Apply one operator. Unknown operators and type-mismatches return false (fail-closed). */
function applyOperator(operator: string, actual: unknown, expected: unknown): boolean {
  switch (operator) {
    case 'equal':
      return actual === expected;
    case 'notEqual':
      return actual !== expected;
    case 'lessThan':
      return numericCompare(actual, expected, (x, y) => x < y);
    case 'lessThanInclusive':
      return numericCompare(actual, expected, (x, y) => x <= y);
    case 'greaterThan':
      return numericCompare(actual, expected, (x, y) => x > y);
    case 'greaterThanInclusive':
      return numericCompare(actual, expected, (x, y) => x >= y);
    case 'in':
      return Array.isArray(expected) && expected.includes(actual);
    case 'notIn':
      return Array.isArray(expected) && !expected.includes(actual);
    case 'contains':
      return Array.isArray(actual) && actual.includes(expected);
    case 'doesNotContain':
      return Array.isArray(actual) && !actual.includes(expected);
    default:
      return false; // unknown operator → fail closed
  }
}

function isGroup(rule: CpsRule): rule is CpsGroup {
  return Array.isArray((rule as CpsGroup).all) || Array.isArray((rule as CpsGroup).any);
}
function isCondition(rule: CpsRule): rule is CpsCondition {
  return typeof (rule as CpsCondition).fact === 'string' && 'operator' in (rule as object);
}

/**
 * Evaluate a cps-rule against a fact context. `{"op":"always"}` is always true; a group is the AND
 * (`all`) / OR (`any`) of its members; a leaf resolves its dotted `fact` and applies its operator.
 * Anything un-evaluatable (unknown shape/operator, missing fact) is false — fail-closed.
 */
export function evaluateCpsRule(rule: CpsRule | null | undefined, facts: CpsFacts): boolean {
  if (!rule || typeof rule !== 'object') return false;
  if (isAlwaysRule(rule)) return true;
  if (isGroup(rule)) {
    const group = rule as CpsGroup;
    if (Array.isArray(group.all)) return group.all.every((r) => evaluateCpsRule(r, facts));
    if (Array.isArray(group.any)) return group.any.some((r) => evaluateCpsRule(r, facts));
    return false;
  }
  if (isCondition(rule)) {
    const actual = resolveFact(facts, rule.fact);
    // A MISSING fact fails EVERY operator — including negated ones (`notEqual`/`notIn`), where a raw
    // `undefined !== value` would otherwise be true and let a conditional win while facts are absent
    // (e.g. vitals/demographics not loaded). Fail-closed: no fact → no match → always-default stays
    // the fallback. [Codex PROD-1384 P2]
    if (actual === undefined) return false;
    return applyOperator(rule.operator, actual, rule.value);
  }
  return false;
}

/** Inputs the encounter can supply as facts at selection time. Extend as more facts are confirmed. */
export interface EncounterFactInputs {
  age?: number;
  gender?: string;
  bmi?: number;
  /** The appointment provider's SpecialitySkillId (tap-time nav; absent on deep-link/reload —
   *  a specialty-conditioned template then fails closed to the general default). PROD-1496. */
  specialtyId?: string;
}

/**
 * Build the fact context from encounter inputs. `bmi` is nested as `{ value }` to match the confirmed
 * live fact path `bmi.value`; age/gender are scalars. Undefined inputs are omitted (→ missing fact →
 * a conditional referencing them is false, so the always-default stays the fallback).
 */
export function buildEncounterFacts(inputs: EncounterFactInputs): CpsFacts {
  const facts: CpsFacts = {};
  if (inputs.age !== undefined) facts.age = inputs.age;
  if (inputs.gender !== undefined) facts.gender = inputs.gender;
  if (inputs.bmi !== undefined) facts.bmi = { value: inputs.bmi };
  if (inputs.specialtyId !== undefined) facts.specialtyId = inputs.specialtyId;
  return facts;
}

/**
 * Leaf-condition count of a rule — the pick's SPECIFICITY proxy (PROD-1496): among conditional
 * templates whose rules all evaluate true, the one with MORE leaf conditions is the more specific
 * match (a specialty template's `{specialtyId==X, age>=0}` beats the general default's `{age>=0}`).
 * `{"op":"always"}` and malformed rules count 0. Interim FE ranking — the durable fix is QB/BFF
 * ranking by scope specificity server-side (flagged on PROD-1496).
 */
export function countRuleLeaves(rule: CpsRule | null | undefined): number {
  if (!rule || typeof rule !== 'object' || isAlwaysRule(rule)) return 0;
  if (isGroup(rule)) {
    const group = rule as CpsGroup;
    const members = Array.isArray(group.all) ? group.all : Array.isArray(group.any) ? group.any : [];
    return members.reduce((n, r) => n + countRuleLeaves(r), 0);
  }
  return isCondition(rule) ? 1 : 0;
}
